import { generatePath } from 'react-router-dom';
import {
  NODON_GENERATION_PROJECT_PATH,
  NODON_PARTIAL_PROJECT_PATH,
  NODON_PROJECT_PATH_STRICT,
  NODON_SHARED_PROJECT_PATH,
} from '@shared/constants/router.constants';

/**
 * Decodes a path segment read from the URL before passing it to {@link generatePath}.
 * React Router's {@link matchPath} leaves percent-encoded characters in param
 * values; passing those back into {@link generatePath} would encode `%` again
 * (e.g. `%3A` → `%253A`) and cause a reroute loop for ids containing `:`.
 */
export const decodeRoutePathParam = (
  value: string | undefined,
): string | undefined => {
  if (value === undefined) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export type PartialProjectPathParams = {
  organization: string;
  projectId: string;
};

export type StrictProjectPathParams = {
  organization: string;
  projectId: string;
  versionId: string;
  page: string;
};

export type SharedProjectPathParams = {
  sharingKey: string;
  versionId: string;
  page: string;
};

export type GenerationProjectPathParams = {
  generationKey: string;
  versionId: string;
  page: string;
};

/**
 * Builds `/:organization/projects/:projectId` from {@link NODON_PARTIAL_PROJECT_PATH}.
 * Prefer this over hand-built path strings so export links and navigation stay in sync.
 */
export const buildPartialProjectPath = (
  params: PartialProjectPathParams,
): string => generatePath(NODON_PARTIAL_PROJECT_PATH, params);

/**
 * Absolute project deep link (`origin` + {@link buildPartialProjectPath}).
 */
export const buildAbsolutePartialProjectUrl = (
  origin: string,
  params: PartialProjectPathParams,
): string => `${origin}${buildPartialProjectPath(params)}`;

/**
 * Builds a canonical strict project pathname from raw param values.
 */
export const buildStrictProjectPath = (
  params: StrictProjectPathParams,
): string => generatePath(NODON_PROJECT_PATH_STRICT, params);

/**
 * Design-view path for a project that was just created.
 * Navigate here directly so create-and-open does not depend on the current route.
 */
export const buildCreatedProjectDesignPath = (params: {
  organization: string;
  projectId: string;
  versionId: string;
}): string =>
  buildStrictProjectPath({
    ...params,
    page: 'design',
  });

/**
 * Same as {@link buildCreatedProjectDesignPath}, but throws when organization or
 * version is missing so the caller can show an error instead of staying on the list.
 */
export const requireCreatedProjectDesignPath = (
  organization: string | undefined,
  projectId: number | string,
  versionId: string | undefined,
): string => {
  if (!organization) {
    throw new Error('Cannot open project without an organization');
  }
  if (!versionId) {
    throw new Error('Created project has no version to open');
  }
  return buildCreatedProjectDesignPath({
    organization,
    projectId: String(projectId),
    versionId,
  });
};

/**
 * Builds a canonical shared project pathname from raw param values.
 */
export const buildSharedProjectPath = (
  params: SharedProjectPathParams,
): string => generatePath(NODON_SHARED_PROJECT_PATH, params);

/**
 * Builds a canonical generation project pathname from raw param values.
 */
export const buildGenerationProjectPath = (
  params: GenerationProjectPathParams,
): string => generatePath(NODON_GENERATION_PROJECT_PATH, params);
